<script setup lang="ts">
import { ref, onMounted, onUnmounted, defineProps, defineEmits } from 'vue';
import { Tax } from '../types';
import { AVAILABLE_TAXES } from '../constants';
import { ChevronDownIcon, CheckIcon } from './Icons.js';

const props = defineProps<{
  selectedTaxes: Tax[];
}>();

const emit = defineEmits<{
  (e: 'change', taxes: Tax[]): void;
}>();

const isOpen = ref(false);
const containerRef = ref<HTMLDivElement | null>(null);

const handleClickOutside = (event: MouseEvent) => {
  if (containerRef.value && !containerRef.value.contains(event.target as Node)) {
    isOpen.value = false;
  }
};

onMounted(() => document.addEventListener('mousedown', handleClickOutside));
onUnmounted(() => document.removeEventListener('mousedown', handleClickOutside));

const toggleTax = (tax: Tax) => {
	const exists = props.selectedTaxes.some(t => t.id === tax.id);

	if (exists) {
		emit('change', []);
	} else {
		emit('change', [tax]);
		isOpen.value = false;
	}
};

const isSelected = (tax: Tax) => props.selectedTaxes.some(t => t.id === tax.id);
</script>

<template>
  <div class="relative w-full" ref="containerRef">
    <button
      type="button"
      @click="isOpen = !isOpen"
      class="flex items-center justify-between w-full h-9 px-2 bg-white border border-slate-200 rounded-md text-sm hover:border-slate-300 focus:ring-2 focus:ring-blue-500 focus:outline-none overflow-hidden"
    >
      <div class="flex-1 text-left overflow-hidden mr-1">
        <span v-if="selectedTaxes.length === 0" class="text-slate-400 font-normal">{{ $t('invoices.no_tax') }}</span>
        <div v-else class="flex flex-wrap gap-1 w-full overflow-hidden h-6 items-center">
          <span
            v-for="tax in selectedTaxes"
            :key="tax.id"
            class="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-100 text-blue-800 whitespace-nowrap"
          >
            {{ $t('invoices.' + tax.name) }} {{ tax.rate }}%
          </span>
        </div>
      </div>
      <ChevronDownIcon class="w-3 h-3 text-slate-400 flex-shrink-0" />
    </button>

    <div v-if="isOpen" class="absolute top-full left-0 z-50 w-48 mt-1 bg-white border border-slate-200 rounded-lg shadow-xl overflow-hidden">
      <div class="py-1">
        <button
          v-for="tax in AVAILABLE_TAXES"
          :key="tax.id"
          type="button"
          @click="toggleTax(tax)"
          class="flex items-center w-full px-3 py-2 text-sm text-left hover:bg-slate-50 transition-colors"
        >
          <div :class="`w-4 h-4 mr-3 rounded border flex items-center justify-center ${isSelected(tax) ? 'bg-blue-600 border-blue-600 text-white' : 'border-slate-300'}`">
            <CheckIcon v-if="isSelected(tax)" class="w-3 h-3" />
          </div>
          <span class="text-slate-700 font-medium flex-1">{{ $t('invoices.' + tax.name) }}</span>
          <span class="text-slate-400 text-xs">{{ tax.rate }}%</span>
        </button>
      </div>
    </div>
  </div>
</template>
